import type { UserConfigurationState } from '../types/userConfiguration';
import { DEFAULT_GEAR_CONFIG } from '../types/GearConfig';

export const initialOnboardingState: UserConfigurationState = {
  // Profile data
  firstName: '',
  lastName: '',
  email: 'test@example.com',
  phone: '',
  
  // Billing config
  billingStructure: null,
  billingStructureConfig: {
    weekStart: 1,
    minimumBillableHours: null,
    halfDayHours: null,
    perWeekDayRateCalculationBasis: 'actual',
  },
  allowPremiumShifts: false,
  premiumShiftOffsetMode: null,
  premiumShiftFixedOffset: null,
  premiumShiftMultiplierCode: null,
  usesShortTurn: false,
  shortTurnConfig: {
    requiredBreakHours: 8,
    surchargeType: 'carry_over',
    shortTurnFixedSurchargeAmount: null,
    surchargeAppliesToShortedHoursOnly: false,
    premiumBasis: 'base_rate' as const,
    premiumType: null,
    premiumValue: 25,
    applyOnHalfDays: true,
    // Unified rate-offset defaults (Phase 9)
    floorSource: 'none',
    overtimeOffset: 1,
    floorTierCode: null,
    floorTierCustomValue: null,
  },
  mealConfig: {
    applyMealSurcharge: false,
    mealDeadlineHours: 5,
    recurringMealDeadline: false,
    mealBreakLengthMinutes: 30,
    breakBillingPolicy: {
      thresholdMinutes: null,
      upToThreshold: 'unpaid',
      overThreshold: 'unpaid',
    },
    mealSurchargeType: 'offset_overtime',
    useAdditionalOffset: true,
    creditIntervalMinutes: 60,
    stepUpAfterMinutes: 60,
    timeCreditMinutes: null,
    fixedFeeSchedule: null,
    fixedFeeAmount: null,
    gracePeriodMinutes: null,
    premiumType: null,
    premiumValue: null,
    premiumBasis: 'STRAIGHT_TIME' as const,
    premiumStacking: 'flat' as const,
    applyOnHalfDays: true,
    mealTriggerMode: 'deadline' as const,
    requiredBreaks: 2,
    thresholdHours: 10,
    additionalBreakIntervalHours: null,
    requiredBreaksHalfDay: null,
    // Unified rate-offset defaults (Phase 9). Defaults map to the
    // `offset_overtime` method (overtime_offset=1); forms must keep these in
    // sync with the selected method so the DB CHECK constraint on
    // user_meal_config holds.
    floorSource: 'none' as const,
    overtimeOffset: 1,
    floorTierCode: null,
    floorTierCustomValue: null,
  },
  overnightConfig: {
    applyOvernightPremium: false,
    startTime: '00:00',
    endTime: '06:00',
    overnightMethod: null,
    thresholdEnabled: false,
    thresholdType: 'hours',
    percentageThreshold: 50,
    hoursThreshold: 4,
    premiumType: null,
    premiumValue: null,
    flatRateAmount: null,
    timeCreditMinutes: null,
    premiumBasis: 'base_rate' as const,
    applyOnHalfDays: true,
    // Unified rate-offset defaults (Phase 9).
    floorSource: 'none' as const,
    overtimeOffset: 0,
    floorTierCode: null,
    floorTierCustomValue: null,
  },
  overtimeConcurrencyStrategy: 'highest_prevailing',
  addFixedSurcharges: false,
  
  // Invoice config
  paymentTermsDays: 30,
  lateFeePercentage: 0,
  useLateFee: false,
  lateFeeType: 'percentage',
  lateFeeFixedAmount: null,
  recurringLateFee: 'once',
  recurringLateFeeInterval: null,
  isRecurringLateFee: false, // This will be derived from recurringLateFee in the component
  useEarlyPaymentDiscount: false,
  earlyPaymentType: 'percentage',
  earlyPaymentFixedAmount: null,
  earlyPaymentDiscountPercentage: 0,
  earlyPaymentDays: 7,
  autoSendReminders: true,
  reminderDays: [7, 3, 1],
  externalPayments: false,
  employmentType: '1099',
  xeroId: null,
  useCancellationPolicy: false,
  cancellationPolicy: [],
  rushPremiumPolicy: [],
  
  // Roles config  
  roles: [],
  
  // Rate multipliers
  rateMultipliers: [
    { 
      code: 'STRAIGHT_TIME',
      name: 'Straight Time',
      defaultMultiplier: 1.0,
      defaultPerShiftThresholdHours: null,
      defaultPerWeekThresholdHours: null,
      perShiftBillingStructures: ['hourly', 'day_rate'],
      perWeekBillingStructures: ['hourly', 'day_rate'],
    }
  ],
  
  // Holiday Premium configuration
  applyHolidayPremium: false,
  holidayCatalogIds: [],
  holidayCustomIds: [],
  countryCodes: ['US'],
  holidayPremiumType: 'offset_overtime',
  // Unified rate-offset defaults (Phase 9)
  holidayFloorSource: 'none',
  holidayOvertimeOffset: 1,
  holidayFloorTierCode: null,
  holidayFloorTierCustomValue: null,
  holidayFixedPremiumAmount: null,
  holidayHourlyPremiumType: null,
  holidayHourlyPremiumValue: null,
  holidayPremiumBasis: 'base_rate' as const,
  holidayApplyOnHalfDays: true,
  surchargeConcurrencyStrategy: 'highest_prevailing',

  // Travel config
  needsTravel: false,
  usesGsaRate: false,
  applyPerDiem: false,
  perDiemBaseRate: null,
  travelDayRateMultiplier: 1.0,
  multiplierAppliesToOverages: null,
  travelDistanceThreshold: null,

  // Expense Config
  expenseConfig: {
    expenseCategories: [],
    expensePolicies: {
      base_policy: {
        allowedCategories: ['Mileage', 'Meals', 'Other'],
        spendingLimits: {},
      }
    },
    expenseMileageRate: 0.67,
    expenseMileageUnit: 'miles',
    defaultExpenseMarkupType: 'none',
    defaultExpenseMarkupPercentage: null,
    defaultExpenseMarkupFixedAmount: null,
    expenseReceiptRequiredAboveAmount: null,
  },

  // Gear / Kit Fees Config
  gearConfig: DEFAULT_GEAR_CONFIG,
};
